import axios from "axios";
import { errorHandler } from "@/Services/errorHandler";
import queryStringify from "qs-stringify";

export default {
  state: {},
  getters: {},
  mutations: {},
  actions: {
    getAccountsData: async (context, data) => {
      let route = data.route;
      let query = data.query;
      let payload = {
        ...query,
        ...context.getters.getTimeFrame,
      };
      let stores = JSON.parse(localStorage.getItem(`stores`));
      let store = JSON.parse(localStorage.getItem(`selected_store`));

      if (stores.length === 0) return;
      let group = stores[0].Group;
      let storeDB = store.StoreDB;

      let headers = {
        group: window.btoa(group),
        DB: storeDB !== null ? window.btoa(storeDB) : null,
      };
      const queryString = `?` + queryStringify(payload);
      return axios
        .get(reportsApi + `${route}` + queryString, {
          headers,
        })
        .then(({ data }) => {
          return data;
        })
        .catch((err) => errorHandler.tomcatError(err));
    },

    /**
     * GET /api/v2/accounts/ledger/transactions
     * Lists general ledger transactions grouped by GroupID
     *
     * Required query params: from, to (from getters.getTimeFrame), user, reason, limit
     * Optional query params: closed (boolean), isReturn (boolean), sort ("Date,DESC" | "Date,ASC" | "GroupID,DESC" | "GroupID,ASC")
     *
     * @param {Object} context
     * @param {Object} params
     * @param {number} params.limit - required by API
     * @param {string} params.user - required by API (username)
     * @param {string} params.reason - required by API (transaction reason/narration filter)
     * @param {boolean} [params.closed]
     * @param {boolean} [params.isReturn]
     * @param {"Date,DESC"|"Date,ASC"|"GroupID,DESC"|"GroupID,ASC"} [params.sort]
     * @returns {Promise<Array<object>>}
     */
    async listLedgerTransactions({ getters }, {
      limit = 50,
      user = "",
      reason = "",
      closed,
      isReturn,
      sort = "Date,DESC",
    } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores")) || [];
      const store = JSON.parse(localStorage.getItem("selected_store")) || {};
      if (!stores.length) return [];

      const headers = {
        DB: store?.StoreDB ? window.btoa(store.StoreDB) : null,
        group: stores[0]?.Group ? window.btoa(stores[0].Group) : undefined,
      };

      // Always include timeframe + requireds, add optionals only when provided
      const timeframe = { ...getters.getTimeFrame };
      const base = { ...timeframe, limit, user, reason };
      const optionals = {};
      if (typeof closed === "boolean") optionals.closed = closed;
      if (typeof isReturn === "boolean") optionals.isReturn = isReturn;
      if (sort) optionals.sort = sort;

      const payload = { ...base, ...optionals };
      const queryString = `?${queryStringify(payload)}`;
      try {
        const { data } = await axios.get(
          `${reportsApi}accounts/ledger/transactions${queryString}`,
          { headers }
        );
        return Array.isArray(data) ? data : [];
      } catch (err) {
        errorHandler.tomcatError(err);
        return [];
      }
    },

    /**
     * GET /api/v2/accounts/ledger/groups/{groupId}
     * Returns the summary and entries that make up a ledger transaction group
     * @param {Object} context
     * @param {string|number} groupId
     * @returns {Promise<{summary: object|null, entries: Array<object>}>>}
     */
    async getLedgerGroupEntries({ getters }, groupId) {
      const stores = JSON.parse(localStorage.getItem("stores")) || [];
      const store = JSON.parse(localStorage.getItem("selected_store")) || {};
      // Allow groupId = 0 (valid). Only block when null/undefined or no stores.
      if (!stores.length || groupId === null || groupId === undefined)
        return { summary: null, entries: [] };

      const headers = {
        DB: store?.StoreDB ? window.btoa(store.StoreDB) : null,
        group: stores[0]?.Group ? window.btoa(stores[0].Group) : undefined,
      };

      const qs = `?${queryStringify({ ...getters.getTimeFrame })}`;
      try {
        const { data } = await axios.get(
          `${reportsApi}accounts/ledger/groups/${groupId}${qs}`,
          { headers }
        );
        // API returns { summary: {...}, entries: [...] }
        if (data && Array.isArray(data.entries)) {
          return { summary: data.summary || null, entries: data.entries };
        }
        // Fallback for older shapes
        if (Array.isArray(data)) {
          return { summary: null, entries: data };
        }
        return { summary: null, entries: [] };
      } catch (err) {
        errorHandler.tomcatError(err);
        return { summary: null, entries: [] };
      }
    },

    /**
     * Fetch all finance departments
     * GET /api/v2/accounts/departments
     * Headers: { DB: base64(StoreDB), group: base64(Group) }
     * @returns {Promise<Array<{DeptId?:string, DeptName:string}>>}
     */
    async listFinanceDepartments({ getters }) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return [];
      const group = stores[0]?.Group;
      const storeDB = store?.StoreDB;

      const payload = { ...getters.getTimeFrame };
      const queryString = `?${queryStringify(payload)}`;
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: group ? window.btoa(group) : undefined,
      };

      try {
        const { data } = await axios.get(
          `${reportsApi}accounts/departments${queryString}`,
          { headers }
        );
        return Array.isArray(data) ? data : [];
      } catch (err) {
        errorHandler.tomcatError(err);
        return [];
      }
    },

    /**
     * Fetch distinct ledger types
     * GET /api/v2/accounts/ledgers/types
     * Response: [{ chartTypes: string }]
     * @returns {Promise<string[]>}
     */
    async listLedgerTypes({ getters }) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return ["All"];
      const group = stores[0]?.Group;
      const storeDB = store?.StoreDB;

      const payload = { ...getters.getTimeFrame };
      const queryString = `?${queryStringify(payload)}`;
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: group ? window.btoa(group) : undefined,
      };

      try {
        const { data } = await axios.get(
          `${reportsApi}accounts/ledgers/types${queryString}`,
          { headers }
        );
        const items = Array.isArray(data) ? data : [];
        const types = items
          .map((x) => x && (x.chartTypes || x.type || x.Type))
          .filter(Boolean);
        return ["All", ...types];
      } catch (err) {
        errorHandler.tomcatError(err);
        return ["All"];
      }
    },

    /**
     * Search ledger names (journals) by type and optional query
     * GET /api/v2/accounts/ledgers/search?type=Expense&q=Suppl&limit=20
     * Response: [{ ChartName: string }]
     * @param {{ type: string, q?: string, limit?: number }} params
     * @returns {Promise<string[]>}
     */
    async searchLedgers({ getters }, { type, q = "", limit = 20 } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return [];
      const group = stores[0]?.Group;
      const storeDB = store?.StoreDB;

      const payload = { ...getters.getTimeFrame, type, q, limit };
      const queryString = `?${queryStringify(payload)}`;
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: group ? window.btoa(group) : undefined,
      };

      try {
        const { data } = await axios.get(
          `${reportsApi}accounts/ledgers/search${queryString}`,
          { headers }
        );
        const arr = Array.isArray(data) ? data : [];
        return ["All", ...arr.map((x) => x && (x.ChartName || x.chartName)).filter(Boolean)];
      } catch (err) {
        errorHandler.tomcatError(err);
        return [];
      }
    },

    async printPDF({ getters }) {
      const payload = { ...getters.getTimeFrame };
      const stores = JSON.parse(localStorage.getItem("stores"));
      if (!stores || stores.length === 0) return;
      const group = stores[0].Group;

      const queryString = `?${queryStringify(payload)}`;
      const headers = { group: window.btoa(group) };

      try {
        const response = await axios.get(
          `${docsApi}stocks/export/pdf/franchiseTurnovers${queryString}`,
          { headers, responseType: "blob" }
        );
        openFile(response, "Franchise Turn Over PDF");
      } catch (err) {
        console.error("Error fetching invoices:", err);
        errorHandler.tomcatError(err);
        throw err;
      }
    },
    async printCSV({ getters }) {
      const payload = { ...getters.getTimeFrame };
      const stores = JSON.parse(localStorage.getItem("stores"));
      if (!stores || stores.length === 0) return;
      const group = stores[0].Group;

      const queryString = `?${queryStringify(payload)}`;
      const headers = { group: window.btoa(group) };

      try {
        const response = await axios.get(
          `${docsApi}stocks/export/excel/franchiseTurnovers${queryString}`,
          { headers, responseType: "blob" }
        );
        openFile(response, "Franchise TurnOver CSV");
      } catch (err) {
        console.error("Error fetching invoices:", err);
        errorHandler.tomcatError(err);
        throw err;
      }
    },

    async getCombinedCreditors({ getters }, query) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));

      if (!stores || stores.length === 0) return;

      const group = stores[0]?.Group;
      if (!group) {
        console.error("Group not found in stores");
        return;
      }

      const storeDB = store?.StoreDB;

      const payload = { ...query, ...getters.getTimeFrame };
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: window.btoa(group),
      };
      const queryString = `?${queryStringify(payload)}`;

      try {
        const { data } = await axios.get(
          `${reportsApi}overview/creditor/acc${queryString}`,
          { headers }
        );

        return data;
      } catch (err) {
        console.error("Error fetching creditors:", err);
        errorHandler.tomcatError(err);
        throw err;
      }
    },

    async getCombinedDebtors({ getters }, query) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));

      if (!stores || stores.length === 0) return;

      const group = stores[0]?.Group;
      if (!group) {
        console.error("Group not found in stores");
        return;
      }

      const storeDB = store?.StoreDB;

      const payload = { ...query, ...getters.getTimeFrame };
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: window.btoa(group),
      };
      const queryString = `?${queryStringify(payload)}`;

      try {
        const { data } = await axios.get(
          `${reportsApi}overview/debtor/acc${queryString}`,
          { headers }
        );

        return data;
      } catch (err) {
        console.error("Error fetching creditors:", err);
        errorHandler.tomcatError(err);
        throw err;
      }
    },

    /**
     * Get aggregated Debtors (A/R) and Creditors (A/P) stats for the selected timeframe.
     * Uses global reportsApi and base64-encoded headers per legacy standard.
     * @returns {Promise<{debtors:{bought:number,paid:number},creditors:{bought:number,paid:number}}>} stats
     */
    async getArApStats({ getters }) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));

      if (!stores || stores.length === 0) return;

      const group = stores[0]?.Group;
      if (!group) {
        console.error("Group not found in stores");
        return;
      }

      const storeDB = store?.StoreDB;
      const payload = { ...getters.getTimeFrame };
      const headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
        group: window.btoa(group),
      };
      const queryString = `?${queryStringify(payload)}`;

      try {
        const { data } = await axios.get(
          `${reportsApi}reports/ar-ap/stats${queryString}`,
          { headers }
        );
        return data;
      } catch (err) {
        errorHandler.tomcatError(err);
        throw err;
      }
    },

    /**
     * List debtor “bought on account” transactions
     * GET /api/v2/reports/ar-ap/debtors/bought/transactions
     */
    async listDebtorsBoughtTx({ getters }, { limit = 200, offset = 0 } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return;
      const group = stores[0] && stores[0].Group; if (!group) return;
      const storeDB = store && store.StoreDB;
      const payload = { ...getters.getTimeFrame, limit, offset };
      const headers = { DB: storeDB ? window.btoa(storeDB) : null, group: window.btoa(group) };
      const queryString = `?${queryStringify(payload)}`;
      try {
        const { data } = await axios.get(`${reportsApi}reports/ar-ap/debtors/bought/transactions${queryString}`, { headers });
        return data;
      } catch (err) { errorHandler.tomcatError(err); throw err; }
    },

    /**
     * List debtor account payments
     * GET /api/v2/reports/ar-ap/debtors/paid/transactions
     */
    async listDebtorsPaidTx({ getters }, { limit = 200, offset = 0 } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return;
      const group = stores[0] && stores[0].Group; if (!group) return;
      const storeDB = store && store.StoreDB;
      const payload = { ...getters.getTimeFrame, limit, offset };
      const headers = { DB: storeDB ? window.btoa(storeDB) : null, group: window.btoa(group) };
      const queryString = `?${queryStringify(payload)}`;
      try {
        const { data } = await axios.get(`${reportsApi}reports/ar-ap/debtors/paid/transactions${queryString}`, { headers });
        return data;
      } catch (err) { errorHandler.tomcatError(err); throw err; }
    },

    /**
     * List creditor bought (supplier invoices / GRVs)
     * GET /api/v2/reports/ar-ap/creditors/bought/transactions
     */
    async listCreditorsBoughtTx({ getters }, { limit = 200, offset = 0 } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return;
      const group = stores[0] && stores[0].Group; if (!group) return;
      const storeDB = store && store.StoreDB;
      const payload = { ...getters.getTimeFrame, limit, offset };
      const headers = { DB: storeDB ? window.btoa(storeDB) : null, group: window.btoa(group) };
      const queryString = `?${queryStringify(payload)}`;
      try {
        const { data } = await axios.get(`${reportsApi}reports/ar-ap/creditors/bought/transactions${queryString}`, { headers });
        return data;
      } catch (err) { errorHandler.tomcatError(err); throw err; }
    },

    /**
     * List creditor payments (supplier payments)
     * GET /api/v2/reports/ar-ap/creditors/paid/transactions
     */
    async listCreditorsPaidTx({ getters }, { limit = 200, offset = 0 } = {}) {
      const stores = JSON.parse(localStorage.getItem("stores"));
      const store = JSON.parse(localStorage.getItem("selected_store"));
      if (!stores || stores.length === 0) return;
      const group = stores[0] && stores[0].Group; if (!group) return;
      const storeDB = store && store.StoreDB;
      const payload = { ...getters.getTimeFrame, limit, offset };
      const headers = { DB: storeDB ? window.btoa(storeDB) : null, group: window.btoa(group) };
      const queryString = `?${queryStringify(payload)}`;
      try {
        const { data } = await axios.get(`${reportsApi}reports/ar-ap/creditors/paid/transactions${queryString}`, { headers });
        return data;
      } catch (err) { errorHandler.tomcatError(err); throw err; }
    },
  },
};
